import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Contexts
import { BundleContext } from "../../context/BundleProvider";
import { ThemeContext } from "../../context/ThemeProvider";
import { useAnalytics } from "../../context/AnalyticsProvider";

// Components
import {
  GlossarySearch,
  GlossaryList,
  GlossaryFilter,
  EmptyGlossaryState
} from "./components/index";
import CommonHeader from "../../components/common/CommonHeader";
import StatsGrid from "../../components/common/StatsGrid";
import { faBook, faFilter, faSearch, faFont } from "@fortawesome/free-solid-svg-icons";

// Constants
import { GET_GLOSSARY } from "../../api/content";
import { STRING } from "../../constant";

let _glossaryTracked = false;

const Glossary = () => {
  const navigate = useNavigate();
  const { selectedCertificate } = useContext(BundleContext);
  const { theme } = useContext(ThemeContext);
  const { track, trackSearch } = useAnalytics();

  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("ALL");
  const [expandedItems, setExpandedItems] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [glossaryData, setGlossaryData] = useState([]);
  
  // Get all glossary data
  const ALL_GLOSSARY = GET_GLOSSARY(selectedCertificate);

  // Track glossary access (once per page load)
  useEffect(() => {
    if (_glossaryTracked) return;
    _glossaryTracked = true;
    track('glossary_viewed', {
      terms_available: ALL_GLOSSARY?.length || 0
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter glossary data
  useEffect(() => {
    filterGlossaryData();
  }, [searchTerm, selectedFilter, selectedCertificate]);

  const filterGlossaryData = () => {
    let filteredData = [...ALL_GLOSSARY];
    const numericRegex = /^[0-9].*$/;

    // Apply alphabet/numeric filter
    if (selectedFilter === "#") {
      filteredData = filteredData.filter(item => 
        numericRegex.test(item.value || "")
      );
    } else if (selectedFilter !== "ALL") {
      filteredData = filteredData.filter(item => 
        (item.value || "")[0]?.toLowerCase() === selectedFilter.toLowerCase()
      );
    }

    // Apply search filter
    if (searchTerm) {
      filteredData = filteredData.filter(item =>
        item.value?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort alphabetically
    filteredData.sort((a, b) => 
      (a.value || "").toLowerCase().localeCompare((b.value || "").toLowerCase())
    );

    setGlossaryData(filteredData);
  };

  // Calculate statistics for StatsGrid
  const calculateStats = () => {
    const totalTerms = ALL_GLOSSARY.length;
    const filteredTerms = glossaryData.length;
    
    // Get alphabet distribution
    const alphabetCounts = {};
    ALL_GLOSSARY.forEach(item => {
      const firstChar = (item.value || "")[0]?.toUpperCase();
      if (firstChar && /[A-Z]/.test(firstChar)) {
        alphabetCounts[firstChar] = (alphabetCounts[firstChar] || 0) + 1;
      }
    });
    
    const mostCommonLetter = Object.entries(alphabetCounts)
      .sort(([,a], [,b]) => b - a)[0];

    const statsData = [
      {
        icon: faBook,
        stat: totalTerms.toLocaleString(),
        label: 'Total Terms',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome'
      },
      {
        icon: faFilter,
        stat: filteredTerms,
        label: 'Showing',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome'
      },
      
    ];

    return statsData;
  };

  const stats = calculateStats();

  // Handle item expansion
  const handleItemToggle = (itemId) => {
    setExpandedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      }
      return [...prev, itemId];
    });
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchTerm(value);
    setExpandedItems([]); // Collapse all when searching
    
    // Track search with debouncing
    if (value && value.length > 2) {
      const timeoutId = setTimeout(() => {
        trackSearch(value, glossaryData.length, {
          search_type: 'glossary'
        });
      }, 1000);
      
      // Clear previous timeout
      if (window.glossarySearchTimeout) {
        clearTimeout(window.glossarySearchTimeout);
      }
      window.glossarySearchTimeout = timeoutId;
    }
  };

  // Handle filter
  const handleFilterSelect = (filter) => {
    setSelectedFilter(filter);
    setIsFilterOpen(false);
    setExpandedItems([]); // Collapse all when filtering
  };

  // Get available filters
  const getAvailableFilters = () => {
    const filters = new Set(['ALL']);
    const numericRegex = /^[0-9].*$/;
    let hasNumeric = false;

    ALL_GLOSSARY.forEach(item => {
      const firstChar = (item.value || "")[0]?.toUpperCase();
      if (firstChar) {
        if (numericRegex.test(item.value)) {
          hasNumeric = true;
        } else if (/[A-Z]/.test(firstChar)) {
          filters.add(firstChar);
        }
      }
    });

    if (hasNumeric) {
      filters.add('#');
    }

    return Array.from(filters).sort((a, b) => {
      if (a === 'ALL') return -1;
      if (b === 'ALL') return 1;
      if (a === '#') return 1;
      if (b === '#') return -1;
      return a.localeCompare(b);
    });
  };

  const availableFilters = getAvailableFilters();

  return (
    <div className="min-h-screen transition-colors duration-300" 
         style={{ background: theme?.background?.main }}>
      <div>
        <CommonHeader
          title="Glossary"
          icon={faBook}
          subtitle="Comprehensive dictionary of certification terms and concepts"
          quote="Knowledge is power. Understanding is wisdom."
          onBack={() => navigate("/home")}
          theme={theme}
          showBackButton={true}
        />
        
        <div className="pb-4">
          <StatsGrid stats={stats} theme={theme} />
        </div>
        
        {/* Search and Filter */}
        <GlossarySearch
          searchTerm={searchTerm}
          onSearch={handleSearch}
          selectedFilter={selectedFilter}
          onFilterClick={() => setIsFilterOpen(true)}
          theme={theme}
        />
        
        {/* Glossary List or Empty State */}
        {glossaryData.length > 0 ? (
          <GlossaryList
            items={glossaryData}
            expandedItems={expandedItems}
            onItemToggle={handleItemToggle}
            searchTerm={searchTerm}
            theme={theme}
          />
        ) : (
          <EmptyGlossaryState
            searchTerm={searchTerm}
            selectedFilter={selectedFilter}
            theme={theme}
          />
        )}
        
        {/* Filter Modal */}
        <GlossaryFilter
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          selectedFilter={selectedFilter}
          onSelectFilter={handleFilterSelect}
          availableFilters={availableFilters}
          theme={theme}
        />
      </div>
    </div>
  );
};

export default Glossary;